<template>
  <div>
    <Title :pages="[{ icon: 'desktop', page: page_title.toUpperCase() }]"></Title>

    <v-container class="dashboard" fluid>
      <!-- Selector de almacén -->
      <v-row>
        <v-col cols="8" md="3">
          <v-combobox
            v-model="filters.almacen"
            name="almacen"
            item-title="name"
            item-value="id"
            :items="almacenes"
            label="Almacen"
            autocomplete="off"
            clearable
            density="compact"
            @update:modelValue="onAlmacenChange"
            :loading="loading_almacenes"
          ></v-combobox>
        </v-col>
      </v-row>

      <!-- RESUMEN -->
      <div class="section-header mb-3">
        <span class="section-label">RESUMEN DEL DÍA</span>
      </div>

      <div class="summary-cards">
        <!-- Card 1: Expedidos -->
        <div class="card">
          <p class="card-title mb-0">Expedidos hoy</p>
          <div class="card-value d-flex align-center">
            <v-skeleton-loader v-if="loading_shipment_status" type="heading" width="56"></v-skeleton-loader>
            <template v-else>
              <span>{{ expedidosCount }}</span>
              <v-tooltip location="bottom">
                <template #activator="{ props }">
                  <v-icon v-bind="props" size="16" class="ms-2 card-info-icon">mdi-information-outline</v-icon>
                </template>
                <div class="canal-tooltip">
                  <div class="canal-tooltip-title">Expedidos por canal de venta</div>
                  <template v-if="expedidos_by_canal.length">
                    <div v-for="c in expedidos_by_canal" :key="c.canal_venta_name" class="canal-tooltip-row">
                      <span>{{ c.canal_venta_name }}</span>
                      <span class="canal-tooltip-amount">{{ c.amount }}</span>
                    </div>
                  </template>
                  <div v-else class="canal-tooltip-empty">Sin expedidos</div>
                </div>
              </v-tooltip>
            </template>
          </div>
          <v-skeleton-loader v-if="loading_shipment_status" type="text" width="100"></v-skeleton-loader>
          <span v-else class="card-badge" :class="getClassPercent(variation_porcentage_shipments)">
            {{ variation_porcentage_shipments === 0 ? '' : variation_porcentage_shipments > 0 ? '↑' : '↓' }}
            {{ Math.abs(variation_porcentage_shipments) }}% {{ periodCompareLabel }}
          </span>
        </div>

        <!-- Card 2: Órdenes trabadas -->
        <div class="card">
          <p class="card-title mb-0">Órdenes trabadas</p>
          <div class="card-value">
            <v-skeleton-loader v-if="loading_shipment_status" type="heading" width="56"></v-skeleton-loader>
            <span v-else :class="stuck_count > 0 ? 'text-danger' : ''">{{ stuck_count }}</span>
          </div>
          <v-skeleton-loader v-if="loading_shipment_status" type="text" width="140"></v-skeleton-loader>
          <template v-else>
            <span v-if="stuck_count > 0" class="card-badge negative">↓ Sin cambio +{{ stuckThresholdLabel }}</span>
            <span v-else class="card-badge neutro">Sin trabadas</span>
          </template>
        </div>

        <!-- Card 3: LPN pendientes de guardado -->
        <div class="card">
          <p class="card-title mb-0">LPN pend. de guardado</p>
          <div class="card-value">
            <v-skeleton-loader v-if="loading_lpn_pending" type="heading" width="56"></v-skeleton-loader>
            <span v-else>{{ lpn_pending.length }}</span>
          </div>
          <v-skeleton-loader v-if="loading_lpn_pending" type="text" width="120"></v-skeleton-loader>
          <template v-else>
            <span v-if="lpnPendingLong > 0" class="card-badge negative">↓ {{ lpnPendingLong }} con +4h</span>
            <span v-else-if="lpn_pending.length > 0" class="card-badge neutro">Sin críticos</span>
            <span v-else class="card-badge neutro">Todo al día</span>
          </template>
        </div>

        <!-- Card 4: ASN pendientes de ingreso -->
        <div class="card">
          <p class="card-title mb-0">ASN pend. de ingreso</p>
          <div class="card-value">
            <v-skeleton-loader v-if="loading_asn_status" type="heading" width="56"></v-skeleton-loader>
            <span v-else>{{ asnPendingCount }}</span>
          </div>
          <v-skeleton-loader v-if="loading_asn_status" type="text" width="120"></v-skeleton-loader>
          <template v-else>
            <span v-if="asnPendingCount > 0" class="card-badge negative">{{ asnPendingCount }} sin recibir</span>
            <span v-else class="card-badge neutro">Sin pendientes</span>
          </template>
        </div>

        <!-- Card 5: Ocupación almacén -->
        <div class="card">
          <div class="card-title">Ocupación almacén</div>
          <div class="card-value">
            <v-skeleton-loader v-if="loading_almacenamiento" type="heading" width="72"></v-skeleton-loader>
            <span v-else>{{ getOcupacionAlmacen() }}%</span>
          </div>
          <v-skeleton-loader v-if="loading_almacenamiento" type="text" width="160"></v-skeleton-loader>
          <template v-else>
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: getOcupacionAlmacen() + '%', backgroundColor: getColorForOcupacion(getOcupacionAlmacen()) }"
              ></div>
            </div>
            <div class="card-subtitle">{{ lpn_con_limite }} / {{ ubicaciones_con_limite ?? 0 }} pallets · {{ lpn_sin_limite }} sin límite</div>
          </template>
        </div>
      </div>

      <!-- Aviso de incidencias no resueltas -->
      <router-link
        v-if="!loading_incidencias && incidencias_not_resolved > 0"
        :to="'/incidencias'"
        class="alert-banner mb-4"
        style="text-decoration: none; cursor: pointer"
      >
        <v-icon class="alert-banner__icon" color="#c62828">mdi-alert-circle-outline</v-icon>
        <span>
          Hay <strong>{{ incidencias_not_resolved }}</strong>
          {{ incidencias_not_resolved === 1 ? 'incidencia no resuelta' : 'incidencias no resueltas' }}.
        </span>
      </router-link>

      <!-- Pipeline + Órdenes y Operarios -->
      <ShipmentOrder
        :status_shipments="status_shipments"
        :loading_shipment_status="loading_shipment_status"
        :loading_shipment_today="loading_shipment_today"
        :shipment_orders="shipment_orders"
        :stuck_hours="stuck_hours"
        :days_back="days_back"
        :operators="operators"
        :loading_operators="loading_operators"
        @stuck-count="onStuckCount"
      />

      <!-- Recepciones -->
      <Recepciones
        :status_reception="status_reception"
        :loading_asn_status="loading_asn_status"
        :loading_asn_today="loading_asn_today"
        :asns="asns"
      />

      <!-- LPNs pendientes de guardado -->
      <LpnPendingStorage
        :lpns="lpn_pending"
        :loading="loading_lpn_pending"
      />
    </v-container>
  </div>
</template>

<script>
import { almacenService } from "@/libs/ws/almacenService";
import Recepciones from "./Recepciones.vue";
import ShipmentOrder from "./ShipmentOrder.vue";
import LpnPendingStorage from "./LpnPendingStorage.vue";
import { dashboardService } from "@/libs/ws/dashboardService";
import { shipmentOrderService } from "@/libs/ws/shipmentOrderService";

export default {
  components: { Recepciones, ShipmentOrder, LpnPendingStorage },
  data() {
    return {
      page_title: "Monitoreo",
      status_reception: [],
      status_shipments: [],
      loading_asn_status: false,
      total_recepciones: 0,
      variation_porcentage_recepciones: 0,
      loading_shipment_status: false,
      total_shipments: 0,
      variation_porcentage_shipments: 0,
      expedidos_by_canal: [],
      loading_shipment_today: false,
      shipment_orders: [],
      loading_asn_today: false,
      asns: [],
      almacenes: [],
      filters: { almacen: null },
      loading_almacenes: false,
      loading_almacenamiento: false,
      lpn_sin_limite: 0,
      lpn_con_limite: 0,
      ubicaciones_sin_limite: 0,
      ubicaciones_con_limite: 0,
      stuck_count: 0,
      lpn_pending: [],
      loading_lpn_pending: false,
      incidencias_not_resolved: 0,
      loading_incidencias: false,
      stuck_hours: 48,
      days_back: 30,
      operators: [],
      loading_operators: false,
      selected_period: "today",
    };
  },
  computed: {
    stuckThresholdLabel() {
      const h = this.stuck_hours;
      if (h < 24) return `${h}h`;
      const days = Math.floor(h / 24);
      const rem  = h % 24;
      return rem > 0 ? `${days}d ${rem}h` : `${days} ${days === 1 ? "día" : "días"}`;
    },
    periodCompareLabel() {
      return "vs ayer";
    },
    expedidosCount() {
      return this.status_shipments.find((s) => s.id_state === shipmentOrderService.STATUS_EXPEDIDO)?.amount ?? 0;
    },
    asnPendingCount() {
      return this.status_reception.find((s) => s.id_state === 'PEN')?.amount ?? 0;
    },
    lpnPendingLong() {
      return this.lpn_pending.filter((l) => {
        if (!l.received_at) return false;
        return (Date.now() - new Date(l.received_at).getTime()) / 1000 / 3600 > 4;
      }).length;
    },
  },
  mounted() {
    this.filters.almacen = JSON.parse(localStorage.getItem("almacen"));
    this.loadAlmacenes();
    if (this.filters.almacen) this.fetchData();
  },
  beforeUnmount() {
    if (this.interval) clearInterval(this.interval);
  },
  methods: {
    getClassPercent(v) {
      if (v === 0) return "neutro";
      return v > 0 ? "positive" : "negative";
    },
    loadAlmacenes() {
      this.loading_almacenes = true;
      this.loading_shipment_status = true;
      this.loading_asn_status = true;
      this.loading_shipment_today = true;
      this.loading_asn_today = true;
      this.loading_almacenamiento = true;

      almacenService
        .filter({})
        .then((response) => {
          this.almacenes = response.result.list;
          if (this.filters.almacen === null) {
            this.filters.almacen = this.almacenes[0];
            this.fetchData();
          }
        })
        .catch((e) => console.error("Error al cargar almacenes", e))
        .finally(() => { this.loading_almacenes = false; });
    },
    fetchData() {
      this.getASNStatusMetrics();
      this.getShipmentOrderStatusMetrics();
      this.getShipmentOrderToday();
      this.getAsnsToday();
      this.getAlmacenamiento();
      this.getOperators();
      this.getLpnPendingStorage();
      this.getIncidenciasNotResolved();
    },
    getIncidenciasNotResolved() {
      this.loading_incidencias = true;
      dashboardService
        .incidencias_not_resolved({ almacen: this.filters.almacen })
        .then((r) => { if (r.status) this.incidencias_not_resolved = r.result.count; })
        .catch((e) => console.error("Error en Incidencias no resueltas", e))
        .finally(() => { this.loading_incidencias = false; });
    },
    getAlmacenamiento() {
      this.loading_almacenamiento = true;
      dashboardService
        .get_almacenamiento({ almacen: this.filters.almacen })
        .then((r) => {
          if (r.status) {
            this.lpn_sin_limite       = r.result.lpn_sin_limite;
            this.lpn_con_limite       = r.result.lpn_con_limite;
            this.ubicaciones_sin_limite = r.result.ubicaciones_sin_limite;
            this.ubicaciones_con_limite = r.result.ubicaciones_con_limite;
          }
        })
        .catch((e) => console.error("Error en Almacenamiento", e))
        .finally(() => { this.loading_almacenamiento = false; });
    },
    getASNStatusMetrics() {
      this.loading_asn_status = true;
      dashboardService
        .states_asn_count({ almacen: this.filters.almacen })
        .then((r) => {
          if (r.status) {
            this.status_reception                 = r.result.status_reception;
            this.total_recepciones                = r.result.total_today;
            this.variation_porcentage_recepciones = r.result.total_variation_percentage;
          }
        })
        .catch((e) => console.error("Error en ASN metrics", e))
        .finally(() => { this.loading_asn_status = false; });
    },
    getShipmentOrderStatusMetrics() {
      this.loading_shipment_status = true;
      dashboardService
        .states_shipment_count({ almacen: this.filters.almacen, period: this.selected_period })
        .then((r) => {
          if (r.status) {
            this.status_shipments               = r.result.status_shipments;
            this.total_shipments                = r.result.total_today;
            this.variation_porcentage_shipments  = r.result.total_variation_percentage;
            this.expedidos_by_canal             = r.result.expedidos_by_canal ?? [];
            if (r.result.stuck_count !== undefined) this.stuck_count = r.result.stuck_count;
          }
        })
        .catch((e) => console.error("Error en Shipment metrics", e))
        .finally(() => { this.loading_shipment_status = false; });
    },
    getAsnsToday() {
      this.loading_asn_today = true;
      dashboardService
        .all_asn({ almacen: this.filters.almacen })
        .then((r) => { if (r.status) this.asns = r.result.list; })
        .catch((e) => console.error("Error en ASN Today", e))
        .finally(() => { this.loading_asn_today = false; });
    },
    getShipmentOrderToday() {
      this.loading_shipment_today = true;
      dashboardService
        .all_shipment_order({ almacen: this.filters.almacen, days_back: this.days_back })
        .then((r) => { if (r.status) this.shipment_orders = r.result.list; })
        .catch((e) => console.error("Error en Shipment Today", e))
        .finally(() => { this.loading_shipment_today = false; });
    },
    getOperators() {
      this.loading_operators = true;
      dashboardService
        .active_operators({
          almacen_id: this.filters.almacen?.id ?? null,
          active_within_hours: 2,
        })
        .then((r) => { if (r.status) this.operators = r.result.operators ?? []; })
        .catch((e) => console.error("Error en Operarios", e))
        .finally(() => { this.loading_operators = false; });
    },
    getColorForOcupacion(p) {
      if (p < 70) return "#4CAF50";
      if (p < 90) return "#FFC107";
      return "#F44336";
    },
    getLpnPendingStorage() {
      this.loading_lpn_pending = true;
      dashboardService
        .lpn_pending_storage({ almacen: this.filters.almacen })
        .then((r) => { if (r.status) this.lpn_pending = r.result.list; })
        .catch((e) => console.error("Error en LPN pending storage", e))
        .finally(() => { this.loading_lpn_pending = false; });
    },
    onAlmacenChange() { this.fetchData(); },
    getOcupacionAlmacen() {
      if (this.ubicaciones_con_limite > 0) {
        return Math.round((this.lpn_con_limite / this.ubicaciones_con_limite) * 100);
      }
      return 0;
    },
    onStuckCount(count) {
      if (this.stuck_count === 0) this.stuck_count = count;
    },
  },
};
</script>
